import { ImperialDistanceUnit, MeasurementSystem } from '../models/measurementSystem';
import { GlobalSettings } from '../models/globalSettings';
import { Utils } from '../services/utils.service';
import { MeasurementUnit } from '../models/measurementUnit';

export class UnitConverter {
	private static measurementSystem: MeasurementSystem;

	static init(measurementSystem: MeasurementSystem): void {
		this.measurementSystem = measurementSystem;
	}

	static getConvertValue(
		value: number,
		unit: MeasurementUnit = 'distance',
		imperialDistanceUnit: ImperialDistanceUnit = 'inches',
		accuracy: number = GlobalSettings.ACCURACY,
		power: number = 1
	): number {
		switch (unit) {
			case 'distance': return UnitConverter.getConvertDistanceValue(value, imperialDistanceUnit, accuracy, power);
			case 'mass': return UnitConverter.getConvertMassValue(value, accuracy);
			default: return value;
		}
	}

	static getSaveConvertValue(
		value: number,
		unit: MeasurementUnit = 'distance',
		imperialDistanceUnit: ImperialDistanceUnit = 'inches',
		accuracy: number = GlobalSettings.ACCURACY,
		power: number = 1
	): number {
		switch (unit) {
			case 'distance': return this.getSaveConvertDistanceValue(value, imperialDistanceUnit, accuracy, power);
			case 'mass': return this.getSaveConvertMassValue(value, accuracy);
			default: return value;
		}
	}

	static getConvertDistanceValue(
		value: number,
		imperialUnit: ImperialDistanceUnit = 'inches',
		accuracy: number = GlobalSettings.ACCURACY,
		power: number = 1
	): number {
		if (!value) return value;

		const imperialUnitCoefficients: Map<ImperialDistanceUnit, number> = new Map([
			['inches', GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.toInches],
			['feet', GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.toFeet]
		]);

		const convertCoefficients: Map<MeasurementSystem, number> = new Map([
			[MeasurementSystem.Metrics, GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.toMeters],
			[MeasurementSystem.Imperial, imperialUnitCoefficients.get(imperialUnit)]
		]);

		if (!convertCoefficients.has(this.measurementSystem)) return value;

		return Utils.round(Math.pow(convertCoefficients.get(this.measurementSystem), power) * value, accuracy);
	}

	static getSaveConvertDistanceValue(
		value: number,
		imperialUnit: ImperialDistanceUnit,
		accuracy: number = GlobalSettings.ACCURACY,
		power: number = 1
	): number {
		if (!value) return value;

		const imperialUnitCoefficients: Map<ImperialDistanceUnit, number> = new Map([
			['inches', GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.fromInches],
			['feet', GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.fromFeet]
		]);


		const convertCoefficients: Map<MeasurementSystem, number> = new Map([
			[MeasurementSystem.Metrics, GlobalSettings.CONVERSION_DISTANCE_COEFFICIENTS.fromMeters],
			[MeasurementSystem.Imperial, imperialUnitCoefficients.get(imperialUnit)]
		]);

		if (!convertCoefficients.has(this.measurementSystem)) return value;

		return Utils.round(Math.pow(convertCoefficients.get(this.measurementSystem), power) * value, accuracy);
	}

	static getConvertMassValue(value: number, accuracy: number = GlobalSettings.ACCURACY): number {
		if (!value) return value;

		const convertCoefficients: Map<MeasurementSystem, number> = new Map([
			[MeasurementSystem.Metrics, GlobalSettings.CONVERSION_MASS_COEFFICIENTS.toKilograms],
			[MeasurementSystem.Imperial, GlobalSettings.CONVERSION_MASS_COEFFICIENTS.toPounds]
		]);

		if (!convertCoefficients.has(this.measurementSystem)) return value;

		return Utils.round(convertCoefficients.get(this.measurementSystem) * value, accuracy);
	}

	static getSaveConvertMassValue(value: number, accuracy: number = GlobalSettings.ACCURACY): number {
		if (!value) return value;

		const convertCoefficients: Map<MeasurementSystem, number> = new Map([
			[MeasurementSystem.Metrics, GlobalSettings.CONVERSION_MASS_COEFFICIENTS.fromKilograms],
			[MeasurementSystem.Imperial, GlobalSettings.CONVERSION_MASS_COEFFICIENTS.fromPounds]
		]);

		if (!convertCoefficients.has(this.measurementSystem)) return value;

		return Utils.round(convertCoefficients.get(this.measurementSystem) * value, accuracy);
	}

	static convertAndRoundToSaveValue(
		value: number,
		unit: MeasurementUnit = 'distance',
		imperialDistanceUnit: ImperialDistanceUnit = 'inches',
		accuracy: number = 2
	): number {
		if (!value) return value;

		let convertedValue: number = this.getConvertValue(value, unit, imperialDistanceUnit);
		convertedValue = Utils.round(convertedValue, accuracy);
		return this.getSaveConvertValue(convertedValue, unit, imperialDistanceUnit);
	}
}